import { Injectable } from '@angular/core';
import { interval, Observable, of, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ChartKeyTypes, ChartPeriods } from '../../market-view/components/index-chart/index-chart.model';
import { TSEIndexDataItem } from '../models/market-data.model';
import {
	BestDemandCollection,
	BestOfMarketCollection,
	BestSupplyCollection,
	ChartResponseModel,
	MdMarketWatchDto,
	PooyaIndexHistoryTrendDto,
	Tse10DaysIndexHistoryDto
} from '../../market-view/models/market-view.model';
import { apiUrls } from '../../../constants';
import { BaseService } from '@easy-lib/modules/shared/services/Base/base.service';

@Injectable({
	providedIn: 'root'
})
export class MarketViewService extends BaseService {
	private marketViewUrl = `${this.mtsPath + apiUrls.easy}MarketView`;
	private IndexHistoryUrl = `${this.mtsPath + apiUrls.easy}market-live/index`;
	mvDataCache: { [key: string]: any } = {};
	dataCacheClearIntervalSubs: { [key: string]: Subscription } = {};

	private dataIsCached(key: string, data: any) {
		this.mvDataCache[key] = data;

		this.dataCacheClearIntervalSubs[key] = interval(30000)
			.pipe(
				take(1),
				tap(() => {
					this.mvDataCache[key] = null;
				})
			)
			.subscribe();
	}

	private getMarketView<T>(resource: string): Observable<T> {
		const url = `${this.marketViewUrl}/${resource}`;

		if (this.mvDataCache[resource]) {
			return of(this.mvDataCache[resource]);
		}

		return this.httpService.get<T>(url).pipe(tap(data => this.dataIsCached(resource, data)));
	}

	private getMarketViewIndexHistory<T>(periodType: ChartPeriods, chartType: ChartKeyTypes): Observable<T> {
		const url = `${this.IndexHistoryUrl}/${chartType}/${periodType}`;

		if (this.mvDataCache[periodType]) {
			return of(this.mvDataCache[periodType]);
		}

		return this.httpService.get<T>(url).pipe(
			tap(data => {
				const x = this.dataIsCached(periodType + chartType, data);
				return x;
			})
		);
	}

	getBestDemand(): Observable<BestDemandCollection> {
		return this.getMarketView<BestDemandCollection>('bestDemand');
	}

	getBestSupply(): Observable<BestSupplyCollection> {
		return this.getMarketView<BestSupplyCollection>('bestSupply');
	}

	getBestVolume(): Observable<BestOfMarketCollection> {
		return this.getMarketView<BestOfMarketCollection>('bestVolume');
	}

	getBestIncrease(): Observable<BestOfMarketCollection> {
		return this.getMarketView<BestOfMarketCollection>('bestIncrease');
	}

	getBestDecrease(): Observable<BestOfMarketCollection> {
		return this.getMarketView<BestOfMarketCollection>('bestDecrease');
	}

	getBestViewed(): Observable<BestOfMarketCollection> {
		return this.getMarketView<BestOfMarketCollection>('bestViewed');
	}

	getOtherIndexes(): Observable<TSEIndexDataItem[]> {
		return this.getMarketView<TSEIndexDataItem[]>('otherIndexes');
	}

	getMdMarketWatches(): Observable<MdMarketWatchDto> {
		return this.getMarketView<MdMarketWatchDto>('MdMarketWatch');
	}

	getIndexHistory(): Observable<Tse10DaysIndexHistoryDto> {
		return this.getMarketView<Tse10DaysIndexHistoryDto>('IndexHistory');
	}

	public GetWeeklyIndexTrend = (
		type: ChartKeyTypes = ChartKeyTypes.OTCOverallIndex
	): Observable<PooyaIndexHistoryTrendDto> => {
		return this.getMarketViewIndexHistory<PooyaIndexHistoryTrendDto>(ChartPeriods.weekly, type);
	};

	public GetMonthlyIndexTrend = (
		type: ChartKeyTypes = ChartKeyTypes.OTCOverallIndex
	): Observable<PooyaIndexHistoryTrendDto> => {
		return this.getMarketViewIndexHistory<PooyaIndexHistoryTrendDto>(ChartPeriods.monthly, type);
	};

	public Get3MonthsIndexTrend = (
		type: ChartKeyTypes = ChartKeyTypes.OTCOverallIndex
	): Observable<PooyaIndexHistoryTrendDto> => {
		return this.getMarketViewIndexHistory<PooyaIndexHistoryTrendDto>(ChartPeriods.trippleMonth, type);
	};

	public GetYearlyIndexTrend = (
		type: ChartKeyTypes = ChartKeyTypes.OTCOverallIndex
	): Observable<PooyaIndexHistoryTrendDto> => {
		return this.getMarketViewIndexHistory<PooyaIndexHistoryTrendDto>(ChartPeriods.yearly, type);
	};

	public Get3YearlyIndexTrend = (
		type: ChartKeyTypes = ChartKeyTypes.OTCOverallIndex
	): Observable<PooyaIndexHistoryTrendDto> => {
		return this.getMarketViewIndexHistory<PooyaIndexHistoryTrendDto>(ChartPeriods.trippleYear, type);
	};

	public getDailyIndex = (type: ChartKeyTypes = ChartKeyTypes.OTCOverallIndex): Observable<ChartResponseModel> => {
		return this.getMarketViewIndexHistory<ChartResponseModel>(ChartPeriods.daily, type);
	};
}
